import { AlertTriangle, Pill } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { usePrescriptionRequests } from '@/features/prescription-requests/api';
import {
  RX_REQUEST_KINDS,
  RX_REQUEST_STATUSES,
  type RxRequestFilters,
  type RxRequestKind,
  type RxRequestStatusFilter,
  type StaffRxRequest,
} from '@/features/prescription-requests/types';
import { PrescriptionRequestDrawer } from './PrescriptionRequestDrawer';
import {
  alertCount,
  KIND_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  when,
} from './rx-request-format';
import styles from './PrescriptionRequests.module.css';

const STATUS_OPTIONS = [
  ...RX_REQUEST_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  { value: 'all', label: 'All' },
];
const KIND_OPTIONS = RX_REQUEST_KINDS.map((k) => ({
  value: k,
  label: KIND_LABEL[k],
}));

/**
 * "Refills & outside pharmacy" (RL-1158): refill and outside-pharmacy
 * requests from the patient app. The doctor sees those routed to them plus
 * the branch's unrouted ones, with the medicines; the manager sees the
 * branch without the medicines (`detailsShown: false`) and routes them.
 */
export function PrescriptionRequestsTab({ oversight }: { oversight: boolean }) {
  const { selected, canSelectAll } = useBranch();
  const [filters, setFilters] = useState<RxRequestFilters>({
    status: 'pending',
    kind: '',
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const q = usePrescriptionRequests(filters);
  const noBranch = !selected && !canSelectAll;

  return (
    <Card
      title="Refills & outside pharmacy"
      flush
      actions={
        <div className={styles.filters}>
          <Field label="Status">
            <Select
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  status: e.target.value as RxRequestStatusFilter,
                }))
              }
            />
          </Field>
          <Field label="Kind">
            <Select
              value={filters.kind}
              placeholder="All kinds"
              options={KIND_OPTIONS}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  kind: e.target.value as RxRequestKind | '',
                }))
              }
            />
          </Field>
        </div>
      }
    >
      {oversight && (
        <p className={styles.oversightNote}>
          Oversight view: the medicines, allergies and refusal reasons are shown
          only to the doctor who decides the request. You can route a waiting
          request to a doctor of its branch.
        </p>
      )}
      <DataTable
        caption="Refill and outside-pharmacy requests from the app"
        rows={noBranch ? [] : q.data}
        rowKey={(r) => r.id}
        loading={!noBranch && q.isLoading}
        error={noBranch ? undefined : q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<Pill size={22} />}
            title={
              noBranch
                ? 'Choose a branch to see its requests'
                : filters.status === 'pending'
                  ? 'No requests waiting'
                  : 'No requests match these filters'
            }
            body={
              !noBranch && filters.status === 'pending'
                ? 'Refill and outside-pharmacy requests from the app appear here.'
                : undefined
            }
          />
        }
        columns={[
          {
            key: 'patient',
            header: 'Patient',
            render: (r) => <strong>{r.patient.fullName}</strong>,
          },
          { key: 'kind', header: 'Kind', render: (r) => KIND_LABEL[r.kind] },
          {
            key: 'items',
            header: 'Medicines',
            render: (r) => <ItemsCell r={r} />,
          },
          {
            key: 'requested',
            header: 'Requested',
            render: (r) => when(r.createdAt),
          },
          {
            key: 'routed',
            header: 'Routed to',
            render: (r) => r.routedTo?.fullName ?? 'Branch doctors',
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => (
              <Badge tone={STATUS_TONE[r.status]} dot>
                {STATUS_LABEL[r.status]}
              </Badge>
            ),
          },
          {
            key: 'open',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) => (
              <Button
                size="sm"
                onClick={() => setOpenId(r.id)}
                aria-label={`Open the ${KIND_LABEL[r.kind].toLowerCase()} request from ${r.patient.fullName}`}
              >
                Open
              </Button>
            ),
          },
        ]}
      />
      {openId && (
        <PrescriptionRequestDrawer
          id={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </Card>
  );
}

function ItemsCell({ r }: { r: StaffRxRequest }) {
  if (!r.detailsShown || !r.items)
    return <span className={styles.muted}>Not shown</span>;
  const alerts = alertCount(r.items);
  return (
    <span className={styles.itemsCell}>
      {r.items.length} {r.items.length === 1 ? 'medicine' : 'medicines'}
      {alerts > 0 && (
        <Badge tone="danger" icon={<AlertTriangle size={12} aria-hidden />}>
          Allergy alert
        </Badge>
      )}
    </span>
  );
}
